import styled, { css } from 'styled-components';

import { ArticleSection } from 'editorial/components/ArticleSection';
import { Paragraph } from 'editorial/styles/articleStyles';
import { getNameInitials } from 'editorial/utils/getInitials';
import { variableFontRegular } from 'styles/theme/text';
import { IArticleQuestionAnswerSection, IArticleSectionAdditionalProps } from 'editorial/types';
import { createSanitizedHtmlObject } from 'shared/utils/createSanitizedHtml';
import { useRouter } from 'next/router';
import { localizeUrl } from 'localization/utils/localizeUrl';

export const ArticleQuestionAnswer = ({
  answer,
  author = '',
  category,
  className,
  isInitialsVisible,
  question,
  subject = '',
}: IArticleQuestionAnswerSection & IArticleSectionAdditionalProps & { className?: string }) => {
  const { locale } = useRouter();
  const splitSubject = subject.split(' ');
  const splitAuthor = author.split(' ');

  const doesArticleCategorySupportInitials = ['greatest'].includes(category);

  return (
    <ArticleSection>
      <ArticleQuestionAnswer.Question
        data-qa="interview_question"
        className={className}
        $isInitialsVisible={doesArticleCategorySupportInitials && isInitialsVisible}
        dangerouslySetInnerHTML={createSanitizedHtmlObject(localizeUrl(question, locale))}
        data-before={getNameInitials({ firstName: splitAuthor[0], lastName: splitAuthor[1] })}
      />
      <ArticleQuestionAnswer.Answer
        data-qa="interview_answer"
        className={className}
        $isInitialsVisible={doesArticleCategorySupportInitials && isInitialsVisible}
        dangerouslySetInnerHTML={createSanitizedHtmlObject(localizeUrl(answer, locale))}
        data-before={getNameInitials({ firstName: splitSubject[0], lastName: splitSubject[1] })}
      />
    </ArticleSection>
  );
};

const tabletAndDesktopMixin = css<{ $isInitialsVisible: boolean }>`
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    margin: 18px 0 18px 32px;
    font-size: 18px;
    font-weight: 500;
    line-height: 1.5;

    &::before {
      content: ${({ $isInitialsVisible }) => {
        return $isInitialsVisible ? 'attr(data-before)' : ' ';
      }};
      position: absolute;
      left: -32px;
      top: 0.5em;
      margin-right: 20px;
      font-size: 9.5px;
      letter-spacing: 1.5px;
    }
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    margin: auto auto 18px;

    &::before {
      content: ${({ $isInitialsVisible }) => {
        return $isInitialsVisible ? 'attr(data-before)' : ' ';
      }};
      position: absolute;
      left: -3rem;
    }
  }
`;

ArticleQuestionAnswer.Question = styled(Paragraph)<{ $isInitialsVisible: boolean }>`
  position: relative;
  ${variableFontRegular({ size: '16px', height: '1.44' })};
  margin-top: 4em;
  font-family: 'GOATProductVariableVF', sans-serif;
  font-weight: 500;
  -webkit-font-smoothing: antialiased;
  ${tabletAndDesktopMixin};
`;

ArticleQuestionAnswer.Answer = styled(Paragraph)<{ $isInitialsVisible: boolean }>`
  position: relative;
  ${variableFontRegular({ size: '16px', height: '1.44' })};
  ${tabletAndDesktopMixin};
`;
